import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useRef } from 'react';
import { T } from 'src/shared/helpers';
import CopyIcon from 'src/shared/material_icons/content/content_copy.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete_outline.svg';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import MoveIcon from 'src/shared/material_icons/content/forward.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import styled, { css } from 'styled-components';
import { markMatches } from '../../../configurator-components/TreeComponent';
import { ComposerAreaNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { setComposerRowDragImage } from '../helpers/composerDragImage';
import { getComposerAreaValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';

export type ComposerAreaRowAction = 'move' | 'duplicate' | 'remove';

const Row = styled.div<{ $selected: boolean; $indent: number }>`
    position: relative;
    display: flex;
    width: calc(100% - ${(props) => props.$indent}px);
    min-width: 0;
    height: 34px;
    align-items: center;
    gap: 7px;
    margin-left: ${(props) => props.$indent}px;
    padding: 0 6px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    &::before {
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        display: ${(props) => (props.$selected ? 'block' : 'none')};
        width: 2.5px;
        background: var(--composer-accent, #e8650a);
        border-radius: 2px;
        content: '';
    }

    > div:last-child > * {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }

    ${(props) =>
        props.$selected &&
        css`
            > div:last-child > * {
                opacity: 1;
            }
        `}
`;

const Main = styled.div`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 8px;
`;

const Icon = styled.span`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    color: var(--composer-kind-attribute, #0e7c7c);

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

const Name = styled.span`
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
`;

export const ComposerAreaTreeElement: FC<{
    area: ComposerAreaNode;
    indent: number;
    selected: boolean;
    highlight?: string;
    onSelect: (area: ComposerAreaNode) => void;
    onDoubleClick?: (area: ComposerAreaNode) => void;
    onAction: (action: ComposerAreaRowAction, area: ComposerAreaNode) => void;
    onDragStart: (event: React.DragEvent<HTMLButtonElement>, area: ComposerAreaNode) => void;
    onDragEnd: () => void;
    onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (event: React.DragEvent<HTMLDivElement>, area: ComposerAreaNode) => void;
}> = ({ area, indent, selected, highlight, onSelect, onDoubleClick, onAction, onDragStart, onDragEnd, onDragOver, onDrop }) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const validationMessages = getComposerAreaValidationMessages(area);
    return (
        <Row
            ref={rowRef}
            $indent={indent}
            $selected={selected}
            aria-selected={selected}
            onClick={() => onSelect(area)}
            onDoubleClick={() => onDoubleClick?.(area)}
            onDragOver={onDragOver}
            onDrop={(event) => onDrop(event, area)}
            role='treeitem'
            tabIndex={0}
        >
            <Main>
                <ZTooltip title={T._('Customizable area', 'Admin')}>
                    <Icon>
                        <AreaIcon />
                    </Icon>
                </ZTooltip>
                <Name title={area.name}>{markMatches(area.name, highlight)}</Name>
                {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
            </Main>
            <Actions>
                <DropdownActions
                    header={
                        <ActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </ActionButton>
                    }
                    items={[
                        <DropdownActionItem key='move' icon={<MoveIcon />} onClick={() => onAction('move', area)}>
                            {T._('Move to another attribute…', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem key='duplicate' icon={<CopyIcon />} onClick={() => onAction('duplicate', area)}>
                            {T._('Duplicate', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem key='remove' danger icon={<RemoveIcon />} onClick={() => onAction('remove', area)}>
                            {T._('Remove', 'Admin')}
                        </DropdownActionItem>
                    ]}
                    minWidth='250px'
                    orientation='RIGHT'
                />
                <ZTooltip title={T._('Drag to reorder', 'Admin')}>
                    <ActionButton
                        aria-label={T._('Drag to reorder', 'Admin')}
                        draggable
                        onDragEnd={onDragEnd}
                        onDragStart={(event) => {
                            event.stopPropagation();
                            setComposerRowDragImage(event.dataTransfer, rowRef.current);
                            onDragStart(event, area);
                        }}
                    >
                        <DragIcon />
                    </ActionButton>
                </ZTooltip>
            </Actions>
        </Row>
    );
};
